/**
 * Product-wide constants. The product name lives here and only here: rename the app by
 * changing APP_NAME (and the <title> in apps/web/index.html, which cannot import it).
 * Every text message includes the name, so keep it short: the template budget checks
 * (messages.ts) fail at startup if a longer name no longer fits one segment.
 */
export const APP_NAME = 'EventPing'

/**
 * Who runs EventPing (a sole proprietor). The toll-free registration names this person, so the
 * site says so in the footer, the terms, the privacy policy and /opt-in. Name only: no address.
 */
export const OPERATOR = 'Spencer McWilliams'

/** The public origin in production (eventping.app). APP_ORIGIN overrides it; locally it is the Vite dev server. */
export const PRODUCTION_ORIGIN = 'https://eventping.app'

/**
 * Where the HELP text, the terms and the privacy policy point people: the default for
 * SUPPORT_CONTACT. Cloudflare Email Routing forwards it to a monitored inbox (docs/DEPLOY.md).
 * Any override must be an email address or short URL of at most MAX_SUPPORT_CONTACT characters,
 * so HELP stays one segment.
 */
export const SUPPORT_CONTACT = 'help@eventping.app'
export const MAX_SUPPORT_CONTACT = 33

/** Per-creator caps, enforced by the API. Overridable with LIMIT_* env vars. */
export type Limits = {
  /** Events a creator may have that are not archived. */
  eventsPerCreator: number
  /** People on one event (any channel), not counting removed or denied ones. Email is cheap. */
  contactsPerEvent: number
  /** People on one event who get texts (pending requests included): texts cost money. */
  smsSeatsPerEvent: number
  /** Steps on one event. */
  stepsPerEvent: number
  /**
   * Notify steps on one event (each texts every SMS seat): with 20 seats, at most 200 step
   * texts per event. Quiet steps are free and only count against stepsPerEvent.
   */
  notifyStepsPerEvent: number
  /**
   * Outbound texts one account's events may send in a rolling 30 days: the per-account monthly
   * cap, and the hook for paid tiers later.
   */
  textsPerCreatorPer30Days: number
}

export const DEFAULT_LIMITS: Limits = {
  eventsPerCreator: 3,
  contactsPerEvent: 200,
  smsSeatsPerEvent: 20,
  stepsPerEvent: 30,
  notifyStepsPerEvent: 10,
  textsPerCreatorPer30Days: 500,
}

/**
 * Abuse limits on the public join form (/join/<code>), enforced by the API with counters in
 * SQLite. Overridable with LIMIT_JOIN* env vars (docs/DEPLOY.md). Client addresses are only
 * ever counted by their HMAC (IP_HASH_SECRET); an IPv6 address counts by its /64.
 */
export type JoinLimits = {
  /** Sign-ups (joined or requested) from one client address, across all events, per hour. */
  perIpPerHour: number
  /** The same, per day (rolling 24 hours). */
  perIpPerDay: number
  /** Submissions of any outcome from one client address, per hour: a cheap flood guard. */
  attemptsPerIpPerHour: number
  /** Submissions of any outcome on one event's join links, per hour. */
  attemptsPerEventPerHour: number
  /** Confirmation texts one number may get in a rolling 24 hours, across all events. */
  confirmTextsPerPhonePerDay: number
}

export const DEFAULT_JOIN_LIMITS: JoinLimits = {
  perIpPerHour: 5,
  perIpPerDay: 20,
  attemptsPerIpPerHour: 20,
  attemptsPerEventPerHour: 30,
  confirmTextsPerPhonePerDay: 3,
}

/**
 * The app-wide SMS circuit breaker: outbound texts per UTC day and per UTC month, across every
 * account (SMS_DAILY_CAP, SMS_MONTHLY_CAP). Past a cap no text goes out (except the STOP and
 * HELP replies the carriers require); updates fall back to email, and the operator is emailed
 * once per cap per period.
 */
export const DEFAULT_SMS_DAILY_CAP = 300
export const DEFAULT_SMS_MONTHLY_CAP = 1600 // ≈ $19 at $0.01195: under the $20 AWS spend limit (docs/PRICING.md §4)

/** Field lengths. Message budgets are checked separately, against the real templates. */
/**
 * Event names. Pages show the whole name; a text that has less room shortens it with "..."
 * (messages.ts shortenToFit), so a long name never costs a second segment.
 */
export const MAX_EVENT_TITLE = 80
/**
 * The septets of the event name a step text always keeps (a longer name is shortened to this,
 * or less shortened when the step and note leave room). Step names and notes are budgeted
 * against it: at the production origin that leaves 91 septets for "<step>. <note>".
 */
export const STEP_EVENT_RESERVE = 24
/**
 * The least room every text that names the event must leave for the name under the configured
 * app name, origin and confirmation style; the API refuses to boot below it.
 */
export const MIN_EVENT_IN_TEXT = 16
export const MAX_SENDER_NAME = 16
export const MAX_STEP_TITLE = 60
export const MAX_CONTACT_NAME = 40
export const MAX_THREAD_MESSAGE = 1000
export const MAX_EMAIL = 254

/** Where update emails come from (an SES-verified identity in production). */
export const DEFAULT_EMAIL_FROM = 'EventPing <updates@eventping.app>'

/** One-click email unsubscribe: /u/<token> (page) and POST /api/u/<token> (List-Unsubscribe). */
export const UNSUBSCRIBE_PATH = '/u/'

/** Contact links: the opaque token in /l/<token>. 12 base62 characters is about 71 bits. */
export const LINK_TOKEN_LENGTH = 12
export const LINK_PATH = '/l/'

/**
 * Join links: /join/<code>. An event has one (the creator can rotate or turn it off), and each
 * person the creator adds has a personal one. 8 characters from a 57-letter alphabet with no
 * look-alikes (no 0/O, 1/l/I), about 47 bits: easy to read out, impossible to guess at scale.
 */
export const JOIN_PATH = '/join/'
export const JOIN_CODE_LENGTH = 8

/** How long after triggering a step the texts go out, so a mis-tap can be undone. */
export const DEFAULT_UNDO_WINDOW_MS = 30_000

/** "Lost your link?": the one answer, whether or not the address follows any events. */
export const RECOVER_MESSAGE =
  "If that email follows any events, we've sent the links. Check your inbox in a few minutes."
